import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import {
  ArrowRight, MapPin, Car, Activity, Star, ChevronDown, Receipt,
  PoundSterling, Quote, ShieldCheck, Radio, CalendarClock,
  Calculator, Loader2, AlertCircle, Wallet, Headphones, Compass, Coins,
  Mail, MessageSquare, Wand2, Phone, X,
} from "lucide-react";
import { MockMap } from "../components/MockMap";
import { PlaceAutocomplete } from "../components/PlaceAutocomplete";
import { WebsiteAiBooking } from "../components/WebsiteAiBooking";
import { api } from "../lib/api";
import { SHOW_DEMO_TOOLS, isDemoCapableHost } from "../lib/demoMode";

/* ─────────────────────────────────────────────────────────────────────────
 * EDITABLE PLACEHOLDER COPY
 * All marketing strings live here so they can be swapped out without
 * touching the layout. Replace with real content as it becomes available.
 * ──────────────────────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    quote: "Was on the platform at Basingstoke station in under five minutes. Driver knew exactly where the hospital car park is — saved me a panic.",
    name: "S. Hooper",
    role: "Passenger · Hatch Warren",
    rating: 5,
  },
  {
    quote: "I drive nights. The dispatcher actually understands that an empty trip from the rank costs me. Jobs come in clean, no junk.",
    name: "M. Patel",
    role: "Driver · 4 years",
    rating: 5,
  },
  {
    quote: "Set up our office account on a Tuesday, had VAT receipts in the inbox by Friday. We've moved everything off the old taxi line.",
    name: "Hampshire Logistics Ltd",
    role: "Corporate account",
    rating: 5,
  },
  {
    quote: "Booked at half-ten on a Saturday, expected the worst. Got a fare quote up-front, fixed price, no surge tricks.",
    name: "J. O'Donnell",
    role: "Passenger · Town Centre",
    rating: 5,
  },
];

const FAQ = [
  {
    q: "How is the fare worked out?",
    a: "We quote a price BEFORE you tap Book — base fare plus per-mile rate plus per-minute time. Night rate (22:00–06:00) is applied automatically. Airport runs are flat-priced. The fare you see is the fare you pay; we don't apply surge multipliers behind your back.",
  },
  {
    q: "Can I book in advance?",
    a: "Yes — schedule any ride up to seven days ahead. We automatically promote it to dispatch fifteen minutes before pickup, so a driver is already on the way when your slot arrives.",
  },
  {
    q: "Card or cash?",
    a: "Both. Pay by card in-app (Stripe — same security as your bank), or hand cash on arrival. Corporate accounts are invoiced monthly with a VAT receipt.",
  },
  {
    q: "What's your cancellation policy?",
    a: "Free cancellation any time before a driver accepts. If you cancel after acceptance and the driver is already en route, a small dead-leg fee may apply (currently £4) to cover their lost mileage.",
  },
  {
    q: "What if my driver doesn't arrive?",
    a: "The control room sees every active job. If a driver is delayed or unresponsive, the dispatcher reassigns you to the next nearest unit — usually within ninety seconds. You'll see the swap happen on your map.",
  },
  {
    q: "Where do you cover?",
    a: "Basingstoke and the wider Hampshire patch: Tadley, Overton, Whitchurch, Oakley, Old Basing, Kingsclere. Longer airport runs (Heathrow, Gatwick, Southampton) are flat-priced — quote up-front.",
  },
  {
    q: "I left something in the cab. What now?",
    a: "Tap your last trip in the app and hit \"Report lost property\" — the message goes straight to the driver. Most items are reunited within twenty-four hours.",
  },
  {
    q: "Do you do corporate accounts?",
    a: "Yes — set up a company account and your team books on credit, you get one monthly invoice with VAT, and finance gets a clean CSV export. Contact us via the admin panel to enable.",
  },
];

const demoMarkers = [
  { id: "d1", lat: 51.2664, lng: -1.0879, type: "driver" },
  { id: "d2", lat: 51.2752, lng: -1.0429, type: "driver" },
  { id: "d3", lat: 51.2941, lng: -1.0524, type: "driver" },
  { id: "p1", lat: 51.2683, lng: -1.0884, type: "pickup", label: "STATION" },
  { id: "dr1", lat: 51.2873, lng: -1.0958, type: "dropoff", label: "HOSPITAL" },
];

/* Hard-coded fallback used when /api/public/pricing is unreachable so the
 * page never renders blank fields. Kept in sync with the backend defaults. */
const PRICING_FALLBACK = {
  currency_symbol: "£",
  distance_unit: "mile",
  base_fare: 3.0,
  per_mile_day: 2.20,
  per_mile_night: 2.80,
  night_start_hour: 22,
  night_end_hour: 6,
  per_minute: 0.30,
  minimum_fare: 5.0,
  extras: {
    extra_stop: 1.50,
    pet_or_dog: 2.00,
    airport_meet_and_greet: 5.00,
    waiting_per_minute: 0.30,
  },
};

/* ─────────────────────────────────────────────────────────────────────── */

export default function Landing() {
  // P2 #4 — fetch the operator's public brand snapshot so passengers see
  // the operator's own company name + support address in the contact card
  // and footer. Single fetch on mount; the endpoint is unauthenticated
  // and read-only so this can't possibly affect ride traffic.
  const [brand, setBrand] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/branding`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setBrand(data);
      } catch (e) {
        // landing page must still render if the API is unreachable —
        // every consumer falls back to literal "Crystal Dispatch" copy.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Header title="CRYSTAL DISPATCH" />

      <HeroSection onBookAi={() => setAiOpen(true)} />
      <FeaturesSection />
      <HowItWorksSection />
      <FareCalculatorSection />
      <PricingSection />
      <TestimonialsSection />
      <DriveWithUsSection />
      <FaqSection />
      <ContactSection brand={brand} />

      <footer className="px-6 md:px-12 py-8 bg-[#0A0A0A] text-white" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#FFC107] flex items-center justify-center rounded-sm">
              <Car className="w-4 h-4 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-black text-lg" data-testid="footer-brand-name">
              {(brand?.company_name || "DISPATCH").toUpperCase()}<span className="text-[#FFC107]">.</span>
            </span>
          </div>
          <div className="text-xs text-white/60 font-mono-tab text-center md:text-right" data-testid="footer-meta">
            © 2026 · BASINGSTOKE PRIVATE HIRE · HAMPSHIRE
            <br />
            <Link to="/corporate/apply" className="text-white/80 hover:text-[#FFC107] transition-colors" data-testid="footer-corporate-link">
              Corporate accounts →
            </Link>
            <br />
            <Link to="/request-demo" className="text-white/80 hover:text-[#FFC107] transition-colors" data-testid="footer-request-demo-link">
              Request a demo →
            </Link>
            {brand?.support_email && (
              <>
                <br />
                <a
                  href={`mailto:${brand.support_email}`}
                  className="text-white/80 hover:text-[#FFC107] transition-colors"
                  data-testid="footer-support-mailto"
                >
                  {brand.support_email}
                </a>
              </>
            )}
          </div>
        </div>
      </footer>

      <WebsiteAiBooking open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}

/* ─── HERO ──────────────────────────────────────────────────────────────── */

// Phase A production-hardening: demo creds + the "Admin demo" toggle are
// stripped from production builds. In dev/preview the flag is false and
// the original quick-eval UX is preserved unchanged.
const IS_PRODUCTION_BUILD = (process.env.REACT_APP_ENV || "").trim().toLowerCase() === "production";
// External link to the safe, seeded Demonstration Environment (test data only).
const DEMO_ENV_URL = (process.env.REACT_APP_DEMO_ENV_URL || "").trim();

/* Trust-cue chips below the CTA — short, plain-English claims a passenger
 * cares about. Kept in one array so copy is editable in one place. */
const TRUST_CUES = [
  { icon: PoundSterling, label: "Upfront fares", sub: "Quote before you tap" },
  { icon: Radio, label: "Live driver matching", sub: "Real-time dispatch" },
  { icon: ShieldCheck, label: "Licensed private hire", sub: "Insured & vetted" },
  { icon: CalendarClock, label: "Advance bookings", sub: "Up to 7 days ahead" },
];

const HeroSection = ({ onBookAi }) => {
  const [demoOpen, setDemoOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  return (
    <>
    <section className="border-b border-border bg-white" data-testid="hero-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* Left: hero copy */}
        <div className="lg:col-span-7 px-6 md:px-12 py-14 md:py-20 border-r border-border">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6" data-testid="hero-eyebrow">
              <div className="w-2 h-2 rounded-full bg-[#10B981] pulse-active" />
              <span className="label-mini text-muted-foreground" data-testid="online-status">
                BASINGSTOKE LIVE · PRIVATE HIRE · HAMPSHIRE
              </span>
            </div>

            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]"
              data-testid="hero-title"
            >
              Book reliable<br />
              <span className="bg-[#FFC107] px-2 -mx-1 inline-block -rotate-1">
                private hire
              </span>{" "}
              across<br />
              Basingstoke<span className="text-muted-foreground"> &amp; </span>Hampshire.
            </h1>

            <p
              className="text-base md:text-lg text-muted-foreground mt-7 max-w-xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              Instant fares, live driver matching, advance bookings, and a proper
              control room behind every journey.
            </p>

            {/* Primary CTA dominates; secondary actions are inline links. */}
            <div className="mt-9 flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/register?role=passenger" className="block">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto h-14 px-8 text-base font-bold tracking-wide bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white"
                    data-testid="cta-passenger"
                  >
                    Book a ride
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  onClick={onBookAi}
                  className="w-full sm:w-auto h-14 px-8 text-base font-bold tracking-wide bg-[#002FA7] hover:bg-[#001f6e] text-white"
                  data-testid="cta-ai-booking"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Book by chat
                </Button>
                <a href="tel:+441256438389" className="block" data-testid="cta-phone-booking">
                  <Button
                    type="button"
                    size="lg"
                    onClick={(e) => { e.preventDefault(); setPhoneOpen(true); }}
                    className="w-full sm:w-auto h-14 px-8 text-base font-bold tracking-wide bg-[#0F766E] hover:bg-[#0b5c56] text-white"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Book by phone
                  </Button>
                </a>
              </div>

              <button
                type="button"
                onClick={() => setPhoneOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F766E] hover:underline w-fit"
                data-testid="cta-phone-number"
              >
                <Phone className="w-3.5 h-3.5" />
                Prefer to call? 01256 438389
              </button>

              {DEMO_ENV_URL && !isDemoCapableHost() && (
                <a
                  href={DEMO_ENV_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-col gap-0.5 w-fit border border-border hover:border-[#002FA7] px-4 py-2.5 transition-colors group"
                  data-testid="cta-try-demo"
                >
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-foreground group-hover:text-[#002FA7]">
                    <Compass className="w-4 h-4" /> Try the live demo
                  </span>
                  <span className="text-[11px] text-muted-foreground">Demonstration Environment — test data only</span>
                </a>
              )}

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <Link
                  to="/register?role=driver"
                  className="font-semibold text-foreground hover:text-[#002FA7] flex items-center gap-1 transition-colors"
                  data-testid="cta-driver"
                >
                  Drive with us <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <span className="text-muted-foreground/40">·</span>
                <Link
                  to="/login"
                  className="font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="cta-driver-login"
                >
                  Driver login
                </Link>
                <span className="text-muted-foreground/40">·</span>
                {SHOW_DEMO_TOOLS && (
                  <button
                    onClick={() => setDemoOpen((v) => !v)}
                    className="font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                    data-testid="try-demo-toggle"
                    aria-expanded={demoOpen}
                  >
                    {demoOpen ? "Hide demo logins" : "Admin demo"}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${demoOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                )}
              </div>
            </div>

            {/* Trust cues — single horizontal strip on desktop, 2×2 on mobile */}
            <ul
              className="mt-9 grid grid-cols-2 md:grid-cols-4 gap-3"
              data-testid="trust-cues"
            >
              {TRUST_CUES.map(({ icon: Icon, label, sub }, i) => (
                <li
                  key={label}
                  className="border border-border bg-secondary/20 px-3 py-2.5 flex items-start gap-2"
                  data-testid={`trust-cue-${i}`}
                >
                  <Icon className="w-4 h-4 text-[#002FA7] mt-0.5 flex-shrink-0" strokeWidth={2.25} />
                  <div className="min-w-0">
                    <div className="font-heading text-[13px] font-bold leading-tight">{label}</div>
                    <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                      {sub}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Demo accounts — only shown on demo-capable hosts (preview /
                demonstration environment), never on the live production site. */}
            {SHOW_DEMO_TOOLS && demoOpen && (
              <div
                className="mt-6 pt-5 border-t border-border space-y-3"
                data-testid="demo-accounts-panel"
              >
                <div className="label-mini text-muted-foreground">DEMO LOGINS · EVALUATION ONLY</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 font-mono-tab text-sm">
                  <DemoChip role="Passenger" email="passenger@demo.com" />
                  <DemoChip role="Driver" email="john.driver@test.com" />
                  <DemoChip role="Admin" email="admin@demo.com" />
                </div>
                <div className="text-xs text-muted-foreground font-mono-tab">
                  Password for all: <span className="bg-secondary px-1.5 py-0.5">demo123</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: live map preview */}
        <div className="lg:col-span-5 relative min-h-[400px] lg:min-h-full" data-testid="hero-map">
          <MockMap markers={demoMarkers} className="absolute inset-0" />
          <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2 z-10">
            <StatBlock label="UNITS ONLINE" value="04" accent="#10B981" />
            <StatBlock label="ACTIVE JOBS" value="01" accent="#FFC107" />
            <StatBlock label="AVG PICKUP" value="4 min" accent="#0A0A0A" />
          </div>
        </div>
      </div>
    </section>

      {phoneOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPhoneOpen(false)}
          data-testid="phone-booking-modal"
        >
          <div
            className="bg-white w-full max-w-sm border border-border shadow-2xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPhoneOpen(false)}
              className="absolute top-3 right-3 p-1.5 hover:bg-secondary rounded"
              aria-label="Close"
              data-testid="phone-modal-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 text-[#0F766E] mb-1">
              <Phone className="w-5 h-5" />
              <span className="label-mini">BOOK BY PHONE</span>
            </div>
            <h3 className="font-heading text-2xl font-black tracking-tight">Call to book a taxi</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Our AI voice assistant answers 24/7 — just say where and when. Prefer a person? Say
              "operator" any time.
            </p>
            <a
              href="tel:+441256438389"
              className="mt-4 flex items-center justify-center gap-2 h-14 bg-[#0F766E] hover:bg-[#0b5c56] text-white text-xl font-black tracking-tight transition-colors"
              data-testid="phone-call-now-btn"
            >
              <Phone className="w-5 h-5" /> 01256 438389
            </a>
            <p className="text-[11px] text-muted-foreground mt-2 text-center">
              On mobile this dials automatically. On desktop, dial the number above.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

/* ─── FEATURES (existing) ───────────────────────────────────────────────── */

const FeaturesSection = () => (
  <section className="px-6 md:px-12 py-16 md:py-24 bg-background">
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
        <FeatureCell
          icon={<MapPin />}
          eyebrow="01 / PASSENGER"
          title="Book in seconds."
          body="Fare quoted before you tap. Watch your driver close in on the map. Pay by card or cash on arrival — no fuss, no surprises."
          testId="feature-passenger"
        />
        <FeatureCell
          icon={<Car />}
          eyebrow="02 / DRIVER"
          title="Jobs in one tap."
          body="Sign on, get the nearest job with full pickup, drop-off and fare on screen. Auto-calculated by distance & time at the end."
          testId="feature-driver"
        />
        <FeatureCell
          icon={<Activity />}
          eyebrow="03 / CONTROL ROOM"
          title="Office at a glance."
          body="Every car on a live map, every job in a filterable list. Set fares, surge windows and manage drivers — all in one screen."
          testId="feature-admin"
        />
      </div>
    </div>
  </section>
);

/* ─── HOW IT WORKS (existing) ───────────────────────────────────────────── */

const HowItWorksSection = () => (
  <section className="px-6 md:px-12 py-16 md:py-24 bg-white border-y border-border" data-testid="how-it-works-section">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
        <div>
          <div className="label-mini text-muted-foreground mb-3">HOW IT WORKS</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight max-w-2xl">
            Four taps from kerb to door.
          </h2>
        </div>
        <div className="text-sm text-muted-foreground max-w-md">
          The system finds the closest available driver in Basingstoke. They accept. You watch them come. Everyone gets home.
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border">
        <StepCell n="01" title="Passenger requests" body="Pickup, drop-off, fare estimate." />
        <StepCell n="02" title="System dispatches" body="Nearest available driver gets offer." />
        <StepCell n="03" title="Driver accepts" body="Heads to pickup. Live tracked." />
        <StepCell n="04" title="Trip & pay" body="Auto fare from distance + time." />
      </div>
    </div>
  </section>
);

/* ─── PRICING TRANSPARENCY ──────────────────────────────────────────────── */

const PricingSection = () => {
  const [p, setP] = useState(PRICING_FALLBACK);
  const [live, setLive] = useState(false);

  useEffect(() => {
    api.get("/public/pricing")
      .then(({ data }) => { setP(data); setLive(true); })
      .catch(() => { /* keep fallback values */ });
  }, []);

  const f = (n) => `${p.currency_symbol}${Number(n).toFixed(2)}`;

  return (
    <section className="px-6 md:px-12 py-16 md:py-24 bg-background" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="label-mini text-muted-foreground mb-3 flex items-center gap-2">
              <Receipt className="w-3.5 h-3.5" /> PRICING
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight max-w-2xl">
              No surge<br />no surprises.
            </h2>
          </div>
          <div className="text-sm text-muted-foreground max-w-md">
            Every fare is calculated by distance plus time at the rates below. You see the price <strong>before</strong> you tap Book — never after.
            {live && (
              <span className="block mt-2 text-[11px] font-mono-tab" data-testid="pricing-live-badge">
                · Live rates from dispatcher
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
          {/* Core rates */}
          <div className="bg-white p-6 md:p-8">
            <div className="label-mini text-muted-foreground mb-4 flex items-center gap-1.5">
              <PoundSterling className="w-3 h-3" /> CORE RATES
            </div>
            <dl className="space-y-3 text-sm">
              <PriceRow label="Base fare" value={f(p.base_fare)} testid="price-base" />
              <PriceRow label={`Per ${p.distance_unit} · day`} value={f(p.per_mile_day)} testid="price-per-mile-day" />
              <PriceRow
                label={`Per ${p.distance_unit} · night (${p.night_start_hour}:00–${String(p.night_end_hour).padStart(2,"0")}:00)`}
                value={f(p.per_mile_night)}
                accent={p.per_mile_night !== p.per_mile_day}
                testid="price-per-mile-night"
              />
              <PriceRow label="Per minute (waiting & low speed)" value={f(p.per_minute)} testid="price-per-minute" />
              <PriceRow label="Minimum fare" value={f(p.minimum_fare)} testid="price-min-fare" />
            </dl>
          </div>

          {/* Extras */}
          <div className="bg-white p-6 md:p-8">
            <div className="label-mini text-muted-foreground mb-4">EXTRAS</div>
            <dl className="space-y-3 text-sm">
              <PriceRow label="Extra stop" value={f(p.extras.extra_stop)} testid="price-extra-stop" />
              <PriceRow label="Pet / assistance dog" value={f(p.extras.pet_or_dog)} testid="price-pet-fee" />
              <PriceRow label="Airport meet-and-greet" value={f(p.extras.airport_meet_and_greet)} testid="price-airport-meet" />
              <PriceRow label="Waiting · per minute" value={f(p.extras.waiting_per_minute)} testid="price-waiting" />
            </dl>
            <div className="mt-5 pt-4 border-t border-border text-[11px] text-muted-foreground leading-relaxed">
              Airport flat fares (Heathrow / Gatwick / Southampton) and corporate-account rates are quoted up-front when you enter your route.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PriceRow = ({ label, value, accent, testid }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2 last:border-b-0" data-testid={testid}>
    <dt className="text-muted-foreground">{label}</dt>
    <dd className={`font-mono-tab font-semibold ${accent ? "text-[#002FA7]" : ""}`}>{value}</dd>
  </div>
);

/* ─── TESTIMONIALS ──────────────────────────────────────────────────────── */

const TestimonialsSection = () => (
  <section className="px-6 md:px-12 py-16 md:py-24 bg-white border-y border-border" data-testid="testimonials-section">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
        <div>
          <div className="label-mini text-muted-foreground mb-3">WHAT PEOPLE SAY</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight max-w-2xl">
            Drivers and<br />passengers, both.
          </h2>
        </div>
        <div className="text-xs text-muted-foreground max-w-xs font-mono-tab uppercase" data-testid="testimonials-placeholder-badge">
          ※ Demo copy — real reviews replace these at launch
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
        {TESTIMONIALS.map((t, i) => (
          <TestimonialCard key={t.name} {...t} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const TestimonialCard = ({ quote, name, role, rating, index }) => (
  <div className="bg-white p-6 md:p-8 transition-card" data-testid={`testimonial-${index}`}>
    <Quote className="w-4 h-4 text-[#FFC107] mb-4" strokeWidth={2.5} />
    <p className="text-base leading-relaxed mb-5">"{quote}"</p>
    <div className="flex items-center justify-between pt-4 border-t border-border">
      <div>
        <div className="font-heading font-bold text-sm">{name}</div>
        <div className="label-mini text-muted-foreground">{role}</div>
      </div>
      <div className="flex gap-0.5">
        {Array.from({ length: rating }).map((_, j) => (
          <Star key={j} className="w-3.5 h-3.5 fill-[#FFC107] text-[#FFC107]" />
        ))}
      </div>
    </div>
  </div>
);

/* ─── FAQ ───────────────────────────────────────────────────────────────── */

const FaqSection = () => (
  <section className="px-6 md:px-12 py-16 md:py-24 bg-background" data-testid="faq-section">
    <div className="max-w-3xl mx-auto">
      <div className="mb-12">
        <div className="label-mini text-muted-foreground mb-3">FAQ</div>
        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight">
          Anything else?
        </h2>
      </div>
      <div className="border border-border divide-y divide-border bg-white">
        {FAQ.map((item, i) => (
          <FaqItem key={item.q} index={i} {...item} />
        ))}
      </div>
    </div>
  </section>
);

/**
 * ContactSection — P2 #4 (2026-06).
 *
 * Operator-brand-aware contact card. Renders the support + general
 * mailto addresses pulled from /public/branding (with sensible
 * fallbacks if the operator hasn't filled in the OnboardingWizard
 * Branding step yet). Always shows the support address so a passenger
 * who can't find help in the FAQ has a one-tap path to email the
 * operator's actual support inbox — replies route to whoever is
 * configured in operator_settings.support_email.
 */
const ContactSection = ({ brand }) => {
  const companyName = brand?.company_name || "Crystal Dispatch";
  const supportEmail = brand?.support_email || "support@dispatch.crystalworkforce.com";
  const helloEmail = brand?.hello_email || "";
  const tagline = brand?.brand_tagline || "";

  return (
    <section className="px-6 md:px-12 py-16 md:py-24 bg-[#FAFAF7] border-t border-border" data-testid="contact-section">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10">
          <div className="label-mini text-muted-foreground mb-3">SAY HELLO</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight">
            Talk to {companyName}.
          </h2>
          {tagline && (
            <p className="text-base text-muted-foreground mt-3 italic" data-testid="contact-tagline">
              {tagline}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href={`mailto:${supportEmail}`}
            className="group border border-border bg-white p-6 hover:border-[#0A0A0A] transition-colors"
            data-testid="contact-support-card"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center rounded-sm flex-shrink-0 group-hover:bg-[#FFC107] transition-colors">
                <Headphones className="w-5 h-5 text-white group-hover:text-black transition-colors" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="label-mini text-muted-foreground">SUPPORT</div>
                <div className="font-heading font-black text-lg mt-0.5">Need help with a ride?</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Booking issues, lost items, receipts — we usually reply within an hour during operating hours.
                </div>
                <div
                  className="font-mono-tab text-sm text-[#0A0A0A] mt-3 flex items-center gap-1.5 group-hover:underline truncate"
                  data-testid="contact-support-email"
                >
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{supportEmail}</span>
                </div>
              </div>
            </div>
          </a>

          {helloEmail && (
            <a
              href={`mailto:${helloEmail}`}
              className="group border border-border bg-white p-6 hover:border-[#002FA7] transition-colors"
              data-testid="contact-hello-card"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center rounded-sm flex-shrink-0 group-hover:bg-[#FFC107] transition-colors">
                  <MessageSquare className="w-5 h-5 text-white group-hover:text-black transition-colors" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="label-mini text-muted-foreground">SALES &amp; PARTNERSHIPS</div>
                  <div className="font-heading font-black text-lg mt-0.5">Talk to the team</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Corporate accounts, fleet partnerships, press enquiries — we'd love to hear from you.
                  </div>
                  <div
                    className="font-mono-tab text-sm text-[#002FA7] mt-3 flex items-center gap-1.5 group-hover:underline truncate"
                    data-testid="contact-hello-email"
                  >
                    <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{helloEmail}</span>
                  </div>
                </div>
              </div>
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-item-${index}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-secondary/30 transition-colors"
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-heading font-bold text-sm md:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed" data-testid={`faq-answer-${index}`}>
          {a}
        </div>
      )}
    </div>
  );
};

/* ─── SHARED LITTLE BITS ────────────────────────────────────────────────── */

const DemoChip = ({ role, email }) => (
  <div className="border border-border bg-secondary/40 px-3 py-2" data-testid={`demo-chip-${role.toLowerCase()}`}>
    <div className="label-mini text-muted-foreground">{role}</div>
    <div className="text-xs mt-0.5">{email}</div>
  </div>
);

const StatBlock = ({ label, value, accent }) => (
  <div className="bg-white/95 backdrop-blur-sm border border-black/10 px-3 py-2">
    <div className="label-mini text-muted-foreground">{label}</div>
    <div className="font-heading font-black text-xl mt-0.5 flex items-center gap-2">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
      {value}
    </div>
  </div>
);

const FeatureCell = ({ icon, eyebrow, title, body, testId }) => (
  <div className="bg-white p-8 transition-card" data-testid={testId}>
    <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center mb-6 rounded-sm">
      {React.cloneElement(icon, { className: "w-5 h-5", strokeWidth: 2 })}
    </div>
    <div className="label-mini text-muted-foreground mb-3">{eyebrow}</div>
    <h3 className="font-heading text-2xl font-bold tracking-tight mb-3">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

const StepCell = ({ n, title, body }) => (
  <div className="bg-white p-6 md:p-8">
    <div className="font-heading text-5xl md:text-6xl font-black text-[#FFC107] leading-none mb-4">{n}</div>
    <div className="font-heading text-lg font-bold tracking-tight mb-1">{title}</div>
    <div className="text-sm text-muted-foreground">{body}</div>
  </div>
);


/* ─────────────────────────────────────────────────────────────────────────
 *  FARE CALCULATOR — public, no login required
 *  Calls POST /api/public/estimate-fare with the selected pickup + dropoff.
 *  Estimate only — does NOT create a ride.
 * ──────────────────────────────────────────────────────────────────────── */

const FareCalculatorSection = () => {
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [pickupText, setPickupText] = useState("");
  const [dropoffText, setDropoffText] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Prefer selected coordinates; otherwise fall back to typed text (geocoded
  // server-side) so the quote still works when Google Places JS is unavailable.
  const pText = (pickup?.name || pickupText || "").trim();
  const dText = (dropoff?.name || dropoffText || "").trim();
  const canCalc = (pickup?.lat && dropoff?.lat) || (pText && dText);

  const onCalculate = async () => {
    if (!canCalc) return;
    setLoading(true);
    setErr(null);
    setEstimate(null);
    try {
      const body = { vehicle_type: "car" };
      if (pickup?.lat && pickup?.lng) { body.pickup_lat = pickup.lat; body.pickup_lng = pickup.lng; }
      else { body.pickup_text = pText; }
      if (dropoff?.lat && dropoff?.lng) { body.dropoff_lat = dropoff.lat; body.dropoff_lng = dropoff.lng; }
      else { body.dropoff_text = dText; }
      const { data } = await api.post("/public/estimate-fare", body);
      setEstimate(data);
    } catch (e) {
      // Surface the server's clean error detail when present (e.g. UK bounds)
      const msg = e?.response?.data?.detail || "Couldn't fetch an estimate — try again.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-6 md:px-12 py-16 md:py-24 bg-white border-y border-border" data-testid="fare-calculator-section">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <div className="label-mini text-muted-foreground mb-3 flex items-center gap-2">
              <Calculator className="w-3.5 h-3.5" /> INSTANT QUOTE
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight max-w-2xl">
              See your fare<br />before you book.
            </h2>
          </div>
          <div className="text-sm text-muted-foreground max-w-md">
            Enter a pickup and a drop-off. We'll show the actual fare you'd pay — no login, no commitment, no surge tricks.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-border border border-border">
          {/* Inputs (3/5) */}
          <div className="md:col-span-3 bg-white p-6 md:p-8 space-y-4">
            <div>
              <label className="label-mini text-muted-foreground mb-1.5 block">PICKUP</label>
              <PlaceAutocomplete
                value={pickup}
                onChange={setPickup}
                onTextChange={setPickupText}
                placeholder="e.g. Basingstoke Station"
                testId="calc-pickup-input"
                icon={<MapPin className="w-4 h-4 text-[#10B981]" />}
              />
            </div>
            <div>
              <label className="label-mini text-muted-foreground mb-1.5 block">DROP-OFF</label>
              <PlaceAutocomplete
                value={dropoff}
                onChange={setDropoff}
                onTextChange={setDropoffText}
                placeholder="e.g. Heathrow Terminal 5"
                testId="calc-dropoff-input"
                icon={<MapPin className="w-4 h-4 text-destructive" />}
              />
            </div>
            <Button
              onClick={onCalculate}
              disabled={!canCalc || loading}
              className="w-full h-12 mt-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-bold"
              data-testid="calc-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working out your fare…</>
              ) : (
                <>Get instant quote <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
            {!canCalc && !loading && (
              <p className="text-[11px] text-muted-foreground" data-testid="calc-hint">
                Pick both a pickup and a drop-off above to enable the quote.
              </p>
            )}
          </div>

          {/* Result (2/5) */}
          <div className="md:col-span-2 bg-secondary/30 p-6 md:p-8 flex flex-col justify-center">
            {err && (
              <div className="border-2 border-destructive bg-destructive/5 p-3 flex items-start gap-2 text-sm text-destructive" data-testid="calc-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{err}</span>
              </div>
            )}

            {loading && !estimate && (
              <div className="text-center text-muted-foreground" data-testid="calc-loading">
                <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
                <div className="text-sm">Calculating…</div>
              </div>
            )}

            {!loading && !estimate && !err && (
              <div className="text-center text-muted-foreground" data-testid="calc-placeholder">
                <Receipt className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <div className="text-sm">Your quote appears here.</div>
              </div>
            )}

            {estimate && !err && (
              <div data-testid="calc-result">
                <div className="label-mini text-muted-foreground mb-1">{estimate.is_fixed_route ? "FIXED FARE" : "ESTIMATED FARE"}</div>
                <div className="font-heading text-5xl md:text-6xl font-black text-foreground leading-none mb-3" data-testid="calc-result-fare">
                  £{Number(estimate.fare).toFixed(2)}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono-tab">
                  <span data-testid="calc-result-distance">{estimate.distance_mi?.toFixed?.(2) ?? estimate.distance_mi} mi</span>
                  <span>·</span>
                  <span data-testid="calc-result-duration">~{Math.round(estimate.duration_min)} min</span>
                </div>
                {estimate.fare_label && (
                  <div className="mt-2 text-xs text-muted-foreground" data-testid="calc-result-fare-label">{estimate.fare_label}</div>
                )}
                {estimate.is_fixed_route && (
                  <div className="mt-3 inline-block bg-[#FFC107] text-black px-2 py-0.5 text-[11px] font-bold" data-testid="calc-fixed-route">
                    {estimate.fixed_route_name?.toUpperCase()} · FLAT FARE
                  </div>
                )}
                {!estimate.is_fixed_route && estimate.surge_multiplier > 1 && (
                  <div className="mt-3 inline-block bg-[#FFC107] text-black px-2 py-0.5 text-[11px] font-bold" data-testid="calc-surge-badge">
                    LIVE DEMAND · ×{estimate.surge_multiplier.toFixed(1)}
                  </div>
                )}
                <Link to="/register?role=passenger" className="block">
                  <Button
                    className="w-full mt-4 bg-[#FFC107] hover:bg-[#FFB300] text-black font-bold"
                    data-testid="calc-cta-book"
                  >
                    Book this ride <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
                <p className="text-[10px] text-muted-foreground mt-2 text-center leading-tight">
                  Estimate only — final fare confirmed at booking.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};


/* ─────────────────────────────────────────────────────────────────────────
 *  DRIVE WITH US — driver-recruitment section
 *  Visually distinct (dark band) so it reads as "this section is FOR you,
 *  driver" rather than getting lost between passenger sections.
 * ──────────────────────────────────────────────────────────────────────── */

const DRIVER_BENEFITS = [
  {
    icon: Coins,
    title: "Keep more of every fare",
    body: "Designed to help local drivers take home more per trip than the major apps allow.",
  },
  {
    icon: Wallet,
    title: "Lower circuit fees",
    body: "No expensive weekly rental. Pay only when you're actually working — terms designed for this town.",
  },
  {
    icon: Compass,
    title: "Basingstoke focused",
    body: "Built for Hampshire's roads, regulars and routes. No commute to chase fares — work the patch you know.",
  },
  {
    icon: Headphones,
    title: "Real dispatchers",
    body: "A human in the control room when you need one. Not a help-centre bot 200 miles away.",
  },
  {
    icon: CalendarClock,
    title: "Advance bookings",
    body: "Jobs scheduled days ahead, queued automatically — plan your shift around them.",
  },
  {
    icon: ShieldCheck,
    title: "Flexible work",
    body: "Online when you want. Two hours or twelve — the platform doesn't penalise you for being a part-timer.",
  },
];

const DriveWithUsSection = () => (
  <section
    className="px-6 md:px-12 py-16 md:py-24 bg-[#0A0A0A] text-white"
    data-testid="drive-with-us-section"
  >
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Left: headline + CTA */}
        <div className="lg:col-span-5">
          <div className="label-mini text-[#FFC107] mb-3 flex items-center gap-2">
            <Car className="w-3.5 h-3.5" /> FOR DRIVERS
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
            Drive Basingstoke<br />
            on{" "}
            <span className="bg-[#FFC107] text-black px-2 -mx-1 inline-block -rotate-1">
              your terms.
            </span>
          </h2>
          <p className="text-base text-white/70 mt-6 leading-relaxed max-w-md">
            A local private-hire platform built around the way Hampshire actually drives — not the way Silicon Valley
            thinks driving should work. Lower fees, smarter dispatch, real people in the control room.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register?role=driver">
              <Button
                size="lg"
                className="w-full sm:w-auto h-14 px-8 bg-[#FFC107] hover:bg-[#FFB300] text-black font-bold tracking-wide"
                data-testid="drive-with-us-cta"
              >
                Drive with us <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center h-14 px-6 border border-white/20 hover:border-white/40 text-white text-sm font-semibold transition-colors"
              data-testid="drive-with-us-signin"
            >
              Driver sign-in
            </Link>
          </div>

          <p className="text-[11px] text-white/40 mt-4 font-mono-tab uppercase tracking-wider">
            Licensed private-hire drivers · Hampshire patch
          </p>
        </div>

        {/* Right: benefit grid */}
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {DRIVER_BENEFITS.map((b, i) => (
            <DriverBenefit key={b.title} {...b} index={i} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

const DriverBenefit = ({ icon: Icon, title, body, index }) => (
  <div className="bg-[#0A0A0A] p-5 md:p-6" data-testid={`driver-benefit-${index}`}>
    <div className="w-9 h-9 bg-[#FFC107] flex items-center justify-center mb-4">
      <Icon className="w-4 h-4 text-black" strokeWidth={2.25} />
    </div>
    <div className="font-heading text-base font-bold tracking-tight mb-1">{title}</div>
    <p className="text-[13px] text-white/60 leading-relaxed">{body}</p>
  </div>
);
